"use client";

import { Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../shadcn/ui/dropdown-menu";

import { Comedian } from "@punchup/data/legacy/comedian";
import Image from "next/image";
import { Skeleton } from "../shadcn/ui/skeleton";
import { cn, getCDNImageURL } from "@punchup/utils";
import { useMemo, useState } from "react";
import { SearchInput } from "../shadcn/ui/search-input";

interface ComedianPageSwitcherProps {
  comedians: Comedian[];
  selectedComedianId: string | null;
  onComedianSelect: (comedianId: string) => void;
  isLoading?: boolean;
  isSuperAdmin?: boolean;
  /**
   * When true, render a slim pill-shaped trigger (28px avatar, single-line
   * name, no eyebrow) for use in horizontal headers. Default is the full
   * sidebar-style card trigger.
   */
  compact?: boolean;
}

export function ComedianPageSwitcher({
  comedians,
  selectedComedianId,
  onComedianSelect,
  isLoading = false,
  isSuperAdmin = false,
  compact = false,
}: ComedianPageSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedComedian = useMemo(
    () => comedians.find((c) => c.id === selectedComedianId) ?? null,
    [comedians, selectedComedianId]
  );

  const sortedComedians = useMemo(() => {
    return [...comedians].sort((a, b) =>
      a.display_name.toLowerCase().localeCompare(b.display_name.toLowerCase())
    );
  }, [comedians]);

  const filteredComedians = useMemo(() => {
    if (!search.trim()) return sortedComedians;
    const query = search.toLowerCase();
    return sortedComedians.filter((c) =>
      c.display_name.toLowerCase().includes(query)
    );
  }, [sortedComedians, search]);

  const onlyOneComedian = comedians.length === 1;

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setSearch("");
      }}
    >
      <DropdownMenuTrigger
        asChild
        disabled={isLoading || (onlyOneComedian && !isSuperAdmin)}
      >
        <button
          className={cn(
            "border border-neutral-200 bg-neutral-100 transition-colors duration-200 ease-in-out hover:bg-neutral-200/75",
            compact
              ? "inline-flex items-center gap-2 rounded-full px-2 py-1"
              : "flex w-full flex-row items-center gap-2.5 rounded-lg p-2",
            onlyOneComedian && !isSuperAdmin && "cursor-default"
          )}
          disabled={isLoading || (onlyOneComedian && !isSuperAdmin)}
        >
          {isLoading ? (
            <Skeleton
              className={cn(
                "bg-neutral-200",
                compact ? "h-7 w-7 rounded-full" : "h-11 w-11"
              )}
            />
          ) : selectedComedian?.about_img ? (
            <div
              className={cn(
                "aspect-square shrink-0 overflow-hidden rounded-full bg-black",
                compact ? "h-7 w-7" : "h-11 w-11"
              )}
            >
              <Image
                src={getCDNImageURL(selectedComedian.about_img)}
                alt={selectedComedian.display_name}
                width={100}
                height={100}
                className="h-full w-full aspect-square object-cover"
              />
            </div>
          ) : (
            <div
              className={cn(
                "shrink-0 border border-neutral-300 bg-neutral-200",
                compact ? "h-7 w-7 rounded-full" : "h-11 w-11 rounded-lg"
              )}
            />
          )}
          {compact ? (
            isLoading ? (
              <Skeleton className="bg-neutral-200 h-4 w-24" />
            ) : (
              <p className="max-w-[180px] truncate text-sm font-semibold text-primary-blue">
                {selectedComedian?.display_name}
              </p>
            )
          ) : (
            <div className="flex flex-col items-start gap-[1px] leading-tight select-none flex-1 truncate overflow-ellipsis text-[14px]">
              <p className="font-semibold">Comedian</p>
              {isLoading ? (
                <Skeleton className="mt-[2px] bg-neutral-200 w-24 h-[16px]" />
              ) : (
                <p className="text-left w-full truncate overflow-ellipsis">
                  {selectedComedian?.display_name}
                </p>
              )}
            </div>
          )}
          {onlyOneComedian && !isSuperAdmin ? null : (
            <ChevronDown
              size={compact ? 16 : 24}
              className={cn(
                "transition-transform duration-300 shrink-0",
                open && "rotate-180"
              )}
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] flex flex-col gap-1.5 max-h-[60dvh] overflow-y-scroll"
        align="start"
      >
        {isSuperAdmin && (
          <div className="p-1" onKeyDown={(e) => e.stopPropagation()}>
            <SearchInput
              placeholder="Search comedians..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        {filteredComedians.map((comedian) => (
          <DropdownMenuItem
            key={comedian.id}
            onSelect={() => {
              onComedianSelect(comedian.id);
              setOpen(false);
            }}
            className="cursor-pointer flex flex-row items-center gap-2.5 p-1 text-primary-blue rounded-lg transition-colors duration-200 ease-in-out"
          >
            {comedian.about_img ? (
              <div className="w-11 h-11 aspect-square bg-black flex flex-col items-center justify-center overflow-hidden rounded-full">
                <Image
                  src={getCDNImageURL(comedian.about_img)}
                  alt={comedian.display_name}
                  width={100}
                  height={100}
                  className="h-full w-full aspect-square object-cover"
                />
              </div>
            ) : (
              <div className="w-11 h-11 rounded-lg bg-neutral-200 border border-neutral-300" />
            )}
            <div className="text-[14px] flex flex-col items-start gap-[1px] leading-tight select-none flex-1 truncate overflow-ellipsis">
              <p className="font-semibold">Comedian</p>
              <p className="text-left w-full truncate overflow-ellipsis">
                {comedian.display_name}
              </p>
            </div>
            {comedian.id === selectedComedian?.id && (
              <Check size={16} className="ml-auto" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
